import { supabase } from "@/lib/supabaseClient";
import type {
  BankExportFormat,
  PayrollReturnReason,
} from "@shared/types/payroll";
import type {
  CompanyPayrollPeriod,
} from "./mappers";
import type {
  CombinedPayrollCandidate,
} from "@/types/payroll";
import {
  asErrorMessage,
  assertOk,
  type RpcResult,
} from "./mappers";

/**
 * PERIOD CREATION
 */

export async function ensureScheduledPeriodForCompany(
  companyId: string
): Promise<string> {
  const { error } = await supabase
    .schema("payroll")
    .rpc("ensure_company_payroll_periods", {
      p_company_id: companyId,
    });

  if (error) {
    throw new Error(
      [
        error.message,
        error.details,
        error.hint,
        error.code ? `code=${error.code}` : null,
      ]
        .filter(Boolean)
        .join(" | ")
    );
  }

  return "ensured";
}

export async function getPeriodsForCompany(
  companyId: string
): Promise<CompanyPayrollPeriod[]> {
  const { data, error } = await supabase.rpc(
    "list_company_payroll_periods_enriched",
    {
      p_company_id: companyId,
    }
  );

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  return (data ?? []).map((period: any) => ({
    id: String(period.id),
    company_id: String(period.company_id),
    period_type: String(period.period_type),
    period_start: String(period.period_start),
    period_end: String(period.period_end),
    period_label: String(period.period_label),
    created_at: String(period.created_at),
    created_by: period.created_by == null ? null : String(period.created_by),
    evaluation_group_key:
      period.evaluation_group_key == null
        ? null
        : String(period.evaluation_group_key),
    pay_date: period.pay_date == null ? null : String(period.pay_date),
    has_payroll_run: Boolean(period.has_payroll_run),
    latest_run_id:
      period.latest_run_id == null ? null : String(period.latest_run_id),
    latest_run_state:
      period.latest_run_state == null
        ? null
        : String(period.latest_run_state) as "DRAFT" | "SUBMITTED" | "ISSUED",
    has_issued_run: Boolean(period.has_issued_run),
    latest_issued_at:
      period.latest_issued_at == null ? null : String(period.latest_issued_at),
    latest_return_note:
      period.latest_return_note == null ? null : String(period.latest_return_note),
    latest_return_count:
      period.latest_return_count == null
        ? null
        : Number(period.latest_return_count),
    latest_run_is_returned_to_clerk: Boolean(
      period.latest_run_is_returned_to_clerk
    ),
    has_time_data: Boolean(period.has_time_data),
    period_status:
      period.period_status == null ? "NOT_STARTED" : String(period.period_status),
  }));
}

export async function createPeriod(input: {
  companyId: string;
  period_start: string;
  period_end: string;
  off_schedule_reason: string;
}): Promise<string> {
  const res = await supabase
    .schema("payroll")
    .rpc("create_off_schedule_payroll_period", {
      p_company_id: input.companyId,
      p_period_start: input.period_start,
      p_period_end: input.period_end,
      p_off_schedule_reason: input.off_schedule_reason,
    }) as RpcResult<string>;

  return assertOk(res);
}

/**
 * COMBINED PAYROLL DISCOVERY
 */

export async function listSameLocalWeekPayrollPeriodCandidates(
  periodId: string
): Promise<CombinedPayrollCandidate[]> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("list_same_local_week_payroll_period_candidates", {
      p_period_id: periodId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  return (data ?? []).map((candidate: any) => ({
    id: String(candidate.id),
    company_id: String(candidate.company_id),
    evaluation_group_key:
      candidate.evaluation_group_key == null
        ? null
        : String(candidate.evaluation_group_key),
    period_type: String(candidate.period_type),
    period_start: String(candidate.period_start),
    period_end: String(candidate.period_end),
    pay_date:
      candidate.pay_date == null
        ? null
        : String(candidate.pay_date),
    period_label: String(candidate.period_label),
    has_payroll_run: Boolean(candidate.has_payroll_run),
    latest_run_id:
      candidate.latest_run_id == null
        ? null
        : String(candidate.latest_run_id),
    latest_run_state:
      candidate.latest_run_state == null
        ? null
        : String(candidate.latest_run_state) as
            | "DRAFT"
            | "SUBMITTED"
            | "ISSUED",
    has_issued_run: Boolean(candidate.has_issued_run),
    has_time_data: Boolean(candidate.has_time_data),
    period_status:
      candidate.period_status == null
        ? "NOT_STARTED"
        : String(candidate.period_status),
  }));
}

/**
 * RUN LIFECYCLE
 */

export async function ensureRun(
  periodId: string
): Promise<string> {
  const res = (await supabase
    .schema("payroll")
    .rpc("ensure_run", {
      p_period_id: periodId,
    })) as RpcResult<string>;

  return assertOk(res);
}

export type PayrollSubmitSuccess = {
  ok: true;
  code: string;
  run_id: string;
  period_id?: string;
  message?: string;
};

export type PayrollSubmitFailure = {
  ok: false;
  code: string;
  workflow?: string;
  message: string;
  preflight?: unknown;
  deferable_blockers?: unknown[];
  next_actions?: unknown[];
};

export type PayrollSubmitResult =
  | PayrollSubmitSuccess
  | PayrollSubmitFailure;

export async function submit(
  periodId: string
): Promise<PayrollSubmitResult> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("submit_period", {
      p_period_id: periodId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  if (!data || typeof data !== "object") {
    throw new Error(
      "Payroll submission returned an invalid response."
    );
  }

  if (data.ok === true && data.run_id) {
    return {
      ok: true,
      code: String(data.code ?? "SUBMITTED"),
      run_id: String(data.run_id),
      period_id:
        data.period_id == null
          ? undefined
          : String(data.period_id),
      message:
        data.message == null
          ? undefined
          : String(data.message),
    };
  }

  return {
    ok: false,
    code:
      data.code == null
        ? "SUBMISSION_BLOCKED"
        : String(data.code),
    workflow:
      data.workflow == null
        ? undefined
        : String(data.workflow),
    message:
      data.message == null
        ? "Payroll submission could not be completed."
        : String(data.message),
    preflight:
      data.preflight == null
        ? undefined
        : data.preflight,
    deferable_blockers: Array.isArray(data.deferable_blockers)
      ? data.deferable_blockers
      : [],
    next_actions: Array.isArray(data.next_actions)
      ? data.next_actions
      : [],
  };
}

export async function deferOpenTimeDisputeToNextPeriod(input: {
  disputeId: string;
  currentPeriodId: string;
}): Promise<void> {
  const { error } = await supabase
    .schema("payroll")
    .rpc("defer_open_time_dispute_to_next_period", {
      p_dispute_id: input.disputeId,
      p_current_period_id: input.currentPeriodId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }
}

/**
 * RETURN FLOW
 */

export async function returnToClerk(
  runId: string,
  reason: PayrollReturnReason,
  note: string
): Promise<void> {
  const { error } = await supabase
    .schema("payroll")
    .rpc("return_run_to_clerk", {
      p_run_id: runId,
      p_reason: reason,
      p_note: note,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }
}

/**
 * ISSUE FLOW
 */

export async function issue(
  runId: string
): Promise<void> {
  const { error } = await supabase
    .schema("payroll")
    .rpc("issue_run", {
      p_run_id: runId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }
}

/**
 * BANK EXPORT
 */

export async function createBankExport(
  runId: string,
  format: BankExportFormat
): Promise<string> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("create_bank_export", {
      p_run_id: runId,
      p_format:
        format === "NACHA_PPD_CREDITS"
          ? "NACHA_PPD_CREDITS"
          : "CSV",
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  return String(data);
}